import React, { useEffect, useRef, useState } from "react";
import { Home, Upload, FileText, User, LogOut, ChevronDown, ChevronLeft, ChevronRight, Bell, Languages } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import NavItem from "./NavItem";
import authService from "../services/authService";
import { useSidebar } from "../contexts/SidebarContext";
import { useLanguage } from "../contexts/LanguageContext";

const Sidebar = ({ notificationsCount = 0, onNotificationsClick }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
  const userMenuRef = useRef(null);
  const { language, setLanguage, t } = useLanguage();

  // Charger les infos utilisateur depuis le stockage local
  useEffect(() => {
    setUser(authService.getUserData());
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        setUser(authService.getUserData());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userName = authService.getUserName() || 'Utilisateur';
  const userRole = authService.getUserRole() || 'Lecteur PDF';
  const initials = (userName || 'U')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager && authService.isManager();

  // Sur mobile, on force le menu en version étendue (texte + largeur max)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveCollapsed = isMobile ? false : isCollapsed;

  const handleNavItemClick = () => {
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const handleProfile = () => {
    setMenuOpen(false);
    navigate('/profile');
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await authService.logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <>
      {/* Overlay mobile pour fermer le menu */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside className={`${effectiveCollapsed ? 'w-16' : 'w-64'} h-screen bg-white border-r flex flex-col fixed left-0 top-0 bottom-0 z-50 transition-all duration-300 transform ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      {/* Toggle Button - Positioned on the right edge (desktop uniquement) */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-12 bg-white border border-gray-200 rounded-r-lg shadow-md hover:bg-gray-50 hidden md:flex items-center justify-center text-gray-600 transition-colors z-10"
        title={isCollapsed ? "Ouvrir le menu" : "Fermer le menu"}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
      
      {/* Logo */}
      <div>
        <div className={`flex flex-col items-center ${effectiveCollapsed ? 'p-2' : 'p-6'}`}>
          {!effectiveCollapsed && (
            <>
              <img src="/total-energies-logo.png" alt="Total Energies Logo" className="w-20 h-20 object-contain mb-3" />
              <div className="text-center">
                <h1 className="text-lg font-bold">DigitalBook</h1>
                <p className="text-sm text-gray-500">Lecture de vos documents en toutes simplicité</p>
              </div>
            </>
          )}
          {effectiveCollapsed && (
            <img src="/total-energies-logo.png" alt="Total Energies Logo" className="w-12 h-12 object-contain" />
          )}
        </div>
        
        {/* Nav */}
        <nav className="flex flex-col gap-1">
          <div className="hidden md:block">
            <NavItem
              icon={
                <div className="relative">
                  <Bell size={18} className="w-4 h-4" />
                  {notificationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[10px] px-1 min-w-[16px] text-center">
                      {notificationsCount > 9 ? '9+' : notificationsCount}
                    </span>
                  )}
                </div>
              }
              label={t('sidebar.notifications')}
              active={false}
              isCollapsed={effectiveCollapsed}
              onClick={onNotificationsClick}
            />
          </div>
          <NavLink to="/" className="no-underline" onClick={handleNavItemClick}>
            {({ isActive }) => (
              <NavItem 
                icon={<Home size={18} />} 
                label={t('sidebar.home')} 
                active={isActive}
                isCollapsed={effectiveCollapsed}
              />
            )}
          </NavLink>
          {isAdmin && (
            <NavLink to="/upload" className="no-underline" onClick={handleNavItemClick}>
              {({ isActive }) => (
                <NavItem 
                  icon={<Upload size={18} />} 
                  label={t('sidebar.uploadPdf')} 
                  active={isActive}
                  isCollapsed={effectiveCollapsed}
                />
              )}
            </NavLink>
          )}
          {(isAdmin || isManager) && (
            <NavLink to="/admin/users" className="no-underline" onClick={handleNavItemClick}>
              {({ isActive }) => (
                <NavItem 
                  icon={<User size={18} />} 
                  label={t('sidebar.users')} 
                  active={isActive}
                  isCollapsed={effectiveCollapsed}
                />
              )}
            </NavLink>
          )}
          <NavLink to="/documents" className="no-underline" onClick={handleNavItemClick}>
            {({ isActive }) => (
              <NavItem 
                icon={<FileText size={18} />} 
                label={t('sidebar.myDocuments')} 
                active={isActive}
                isCollapsed={effectiveCollapsed}
              />
            )}
          </NavLink>
        </nav>
      </div>

      {/* Langue - desktop (au-dessus du profil) */}
      {!effectiveCollapsed && (
        <div className="px-4 pt-4 pb-0 border-t hidden md:block mt-auto">
          <div className="text-sm font-medium text-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <Languages size={16} className="text-gray-500" />
              <span>{t('sidebar.languageLabel')}</span>
            </div>
            <div className="flex justify-center gap-2">
              {['fr', 'en', 'pt'].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLanguage(code)}
                  className={`px-2 py-1 rounded-full border text-xs ${
                    language === code
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-200'
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* User (desktop seulement) */}
      <div className="px-4 pt-0 pb-4 relative hidden md:block" ref={userMenuRef}>
        <button
          type="button"
          className={`w-full flex items-center ${effectiveCollapsed ? 'justify-center' : 'gap-3'} p-2 rounded-lg hover:bg-gray-50`}
          onClick={() => setMenuOpen((o) => !o)}
          title={effectiveCollapsed ? userName : ''}
        >
          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-semibold">{initials || 'U'}</div>
          {!effectiveCollapsed && (
            <>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">{userName || 'Utilisateur'}</p>
                <p className="text-xs text-gray-500">{userRole || 'Lecteur PDF'}</p>
              </div>
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>

        {menuOpen && (
          <div className={`absolute ${effectiveCollapsed ? 'left-16' : 'left-2'} bottom-14 w-48 bg-white border rounded-lg shadow-lg py-1 z-50`}>
            <button
              type="button"
              onClick={handleProfile}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <User size={16} /> {t('sidebar.profile')}
            </button>
            <div className="h-px bg-gray-100 my-1" />
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={16} /> {t('sidebar.logout')}
            </button>
          </div>
        )}
      </div>
      {/* Langue - mobile */}
      <div className="p-4 border-t md:hidden">
        <div className="text-sm font-medium text-gray-600">
          <div className="flex items-center gap-2 mb-2">
            <Languages size={16} className="text-gray-500" />
            <span>{t('sidebar.languageLabel')}</span>
          </div>
          <div className="flex justify-center gap-2">
            {['fr', 'en', 'pt'].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLanguage(code)}
                className={`px-2 py-1 rounded-full border text-xs ${
                  language === code
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
